import { memo, useState } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import type { Participant } from '@vc/models'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'

const PASTEL_COLORS = [
  '#E8B8C0', '#B8CFE8', '#B8E8B8', '#CFC0E8', '#E8D0B8',
  '#E8C0D8', '#B8E8D8', '#E8DFB8', '#C8C0E8', '#E8B8B8',
  '#D0E8B8', '#B8E8E8', '#E8D8B8', '#CFC0E8', '#E8C0D8',
  '#B8D8E8', '#E0C0E8', '#B8E8C8', '#E8D0B8', '#C8C0E8',
]

function getAvatarColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return PASTEL_COLORS[Math.abs(hash) % PASTEL_COLORS.length]
}

interface ParticipantSelectorProps {
  participants: Participant[]
  onSelectParticipant: (participant: Participant) => void
  onClose: () => void
}

export const ParticipantSelector = memo(function ParticipantSelector({
  participants,
  onSelectParticipant,
  onClose,
}: ParticipantSelectorProps) {
  const t = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')

  // You can't start a private chat with yourself, so you aren't offered.
  const filteredParticipants = participants.filter(
    (p) => !p.isLocal && p.name.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 animate-in fade-in duration-200">
      <div
        className="w-full max-w-md bg-card rounded-t-2xl shadow-2xl animate-in slide-in-from-bottom duration-300"
        style={{ maxHeight: '70vh' }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-vc-panel-border">
          <h3 className="text-xs font-medium text-slate-800 dark:text-foreground">
            {t('panels.participants')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="grid h-8 w-8 place-items-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition"
          >
            <ChevronDown className="h-5 w-5 text-slate-600 dark:text-muted-foreground" />
          </button>
        </div>

        <div className="px-4 py-3 border-b border-vc-panel-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('panels.searchByName')}
              className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-muted border border-vc-panel-border rounded-lg text-sm text-slate-800 dark:text-foreground placeholder:text-slate-400 dark:placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
            />
          </div>
        </div>

        <div className="overflow-y-auto" style={{ maxHeight: 'calc(70vh - 140px)' }}>
          {filteredParticipants.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-muted-foreground">
              {t('panels.noParticipantsFound')}
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-border/50">
              {filteredParticipants.map((participant) => (
                <button
                  key={participant.id}
                  type="button"
                  onClick={() => {
                    onSelectParticipant(participant)
                    onClose()
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-muted/50 transition text-left"
                >
                  <div
                    className="h-10 w-10 rounded-full flex items-center justify-center text-sm font-semibold text-slate-800 dark:text-foreground shrink-0"
                    style={{ backgroundColor: getAvatarColor(participant.name) }}
                  >
                    {participant.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800 dark:text-foreground truncate">
                      {participant.name}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
})
